/* eslint-disable no-unused-expressions */
/* eslint-disable react/forbid-prop-types */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import _findIndex from 'lodash/findIndex';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isFunction from 'lodash/isFunction';
import _isNil from 'lodash/isNil';
import _isObject from 'lodash/isObject';
import { path as _path } from 'lodash/fp';

import { intlShape } from 'constants/propTypes';
import { getFocusableChildren } from 'helpers/a11y';
import { isFragment, isNativeElement, isFuzzyMatch } from 'helpers/core';
import { bem } from 'helpers/class-name';
import Overlay from 'components/Overlay';

import { DropdownContext } from 'features/App/Contexts';
import messages from './Dropdown.messages';
import './Dropdown.scss';

const { block, element } = bem('dropdown2');

/*
  Dropdowns are built from sub-components. <Dropdown> manages state and passes it
  down to those sub-components. Since the list of options and the selected option
  will come from redux, a standard, <select>-esque, <Dropdown>
  would look like this:

    const choices = [1,2,3,4,5];
    const [picked, setPicked] = useState();

    <Dropdown>
      <DropdownToggle>
        {picked || "pick a number"}
      </DropdownToggle>
      <DropdownMenu>
        {choices.map(choice => (
          <DropdownItem
            key={choice}
            isSelected={choice === picked}
            onClick={() => setPicked(choice)}
          >
            {choice}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>

  The <Dropdown> can also provide an auto-complete input. All that's required...
    - add the `autocomplete` prop - <Dropdown autocomplete ...>
    - add the options and selected-option - <Dropdown options={choices} selectedOption={picked} ...>
    - format options so each has (at a minimum) a `label` and `match` field
      - label - the string that will appear in the input
      - match - a string to compare user-input against
      - isFilterable (opt, default: true) - must the option appear in the suggested
      - ... - all other fields are ignored but passed-through

  The <DropdownMenu> renderProp will receive an object matching the provided
  options plus additional data that can be passed directly to <DropdownItem>.

  Additional fields allow things like displaying a class name, actioning on an ID,
  and matching against both. For example:
    {
      label: 'Nothing but push-ups!',
      match: '12345 Nothing but push-ups!',
      classId: 12345,
    }

    const choices = [
      { label: 'first',  match: 'first 1 one uno' },
      { label: 'second', match: 'second 2 two dos' },
      { label: 'third',  match: 'third 3 three tres' },
      { label: 'fourth', match: 'fourth 4 four quatro' },
      { label: 'fifth',  match: 'fifth 5 five cinco' },
    ];
    const [picked, setPicked] = React.useState({});

    <Dropdown autocomplete options={choices} selectedOption={picked}>
      <DropdownToggle placeholder={messages.pickANumber} />
      <DropdownMenu>
        {data =>
          data.map(({ option, isHighlighted, ref }) => {
            const { label, match } = option;
            return (
              <DropdownItem
                key={match}
                ref={ref}
                isSelected={_isEqual(option, picked)}
                isHighlighted={isHighlighted}
                onClick={() => setPicked(option)}
              >
                {label}
              </DropdownItem>
            );
          })
        }
      </DropdownMenu>
    </Dropdown>

  Use the <DropdownItemCheckbox> component to make a multi-select dropdown. Setting
  the `isSelectAll` prop will automatically display the "Select all" message.
  Adding the `multiselect` prop to the Dropdown makes the key-events more checkbox-friendly.

    const options = [1, 2, 3];
    const selectedOptions = [1, 2, 3];
    const allSelected = options.length === selectedOptions.length;

    <Dropdown closeOnSelect={false} multiselect>
      <DropdownToggle label={messages.label}>
        <Message
          {...(allSelected ? messages.allSelected : messages.xSelected)}
          values={{ count: selectedOptions.length }}
        />
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItemCheckbox
          isSelectAll
          isChecked={allSelected}
          onClick={() => updateSelectedOptions(allSelected ? [] : options)}
        />
        {options.map(option => (
          <DropdownItemCheckbox
            key={option}
            isChecked={selectedOptions.includes(option)}
            onClick={checked => {
              const updatedArr = checked
                ? selectedOptions.concat(venueId)
                : selectedOptions.filter(opt => opt !== option);
              updateSelectedOptions(updatedArr);
            }}>
            {option}
          </DropdownItemCheckbox>
        ))}
      </DropdownMenu>
    </Dropdown>

  <Dropdown> also provides a renderProp that makes it easy to build completely
  custom toggles and contents.

    <Dropdown>
      {({ toggleMenu, closeMenu }) => (
        <>
          <button type="button" onClick={toggleMenu}>
            Show image
          </button>
          <img src="https://i.picsum.photos/id/724/400/300.jpg" onClick={closeMenu} />
        </>
      )}
    </Dropdown>
*/

class Dropdown extends Component {
  static instanceCount = 0;

  static propTypes = {
    attachment: PropTypes.oneOf(['left', 'pinRight', 'right', 'center']),
    autocomplete: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    className: PropTypes.string,
    closeOnClick: PropTypes.bool,
    closeOnSelect: PropTypes.bool,
    contentClassName: PropTypes.string,
    dataCypress: PropTypes.string,
    deriveContentWidth: PropTypes.func,
    disabled: PropTypes.bool,
    closeOnEsc: PropTypes.bool,
    intl: intlShape,
    isDesktop: PropTypes.bool,
    isAlignTopToggleStyle: PropTypes.bool,
    isOpen: PropTypes.bool,
    isLargeRadiusStyle: PropTypes.bool,
    isSmallRadiusStyle: PropTypes.bool,
    multiselect: PropTypes.bool,
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.any),
    optionsFilter: PropTypes.func,
    portalId: PropTypes.string,
    closeOnScroll: PropTypes.bool,
    selectedOption: PropTypes.oneOfType([PropTypes.any]), // lint
    showMenuCaret: PropTypes.bool,
    useOverlay: PropTypes.bool,
    usePortal: PropTypes.bool,
  };

  static defaultProps = {
    attachment: 'center',
    autocomplete: false,
    closeOnClick: true,
    closeOnSelect: true,
    dataCypress: 'dropdown',
    closeOnEsc: true,
    isOpen: false,
    options: [],
    optionsFilter: isFuzzyMatch,
    portalId: 'dropdown__host',
    closeOnScroll: true,
    showMenuCaret: false,
    selectedOption: null,
    useOverlay: true,
    usePortal: true,
  };

  static getDerivedStateFromProps(props, state) {
    const { options, selectedOption } = props;
    const { previousOptions, filteredOptions } = state;
    let { highlightedOption } = state;

    const updatedFilterOptions = !_isEqual(previousOptions, options) ? options : filteredOptions;

    const highlightedIsEmptyCollection =
      (_isObject(highlightedOption) || _isArray(highlightedOption)) && _isEmpty(highlightedOption);
    const highlightedIsNil = _isNil(highlightedOption);
    const hasSelected = !_isEmpty(selectedOption);

    if ((highlightedIsEmptyCollection || highlightedIsNil) && hasSelected) {
      highlightedOption = selectedOption;
    }

    return {
      ...state,
      highlightedOption,
      previousOptions: options,
      filteredOptions: updatedFilterOptions,
    };
  }

  constructor(props) {
    super(props);

    const { isOpen, options } = props;

    this.state = {
      isOpen,
      inputValue: null,
      contentDoesntFitBelowToggle: false,
    };

    /* eslint-disable-next-line no-multi-assign */
    this.instanceId = Dropdown.instanceCount += 1; // unique for each instance across app
    this.toggleId = `Dropdown_${this.instanceId}_Toggler`;
    this.menuId = `Dropdown_${this.instanceId}_Menu`;
    this.containerRef = React.createRef();
    this.scrollContainerRef = React.createRef();
    this.inputRef = React.createRef();
    this.itemRefs = this.makeRefsForOptions(options);
  }

  componentDidUpdate(prevProps) {
    const { options: prevOptions } = prevProps;
    const { options } = this.props;

    if (!_isEqual(prevOptions, options)) {
      this.itemRefs = this.makeRefsForOptions(options);
    }
    this.adjustSizeAndPosition();
  }

  getToggleAndContent() {
    const { children, disabled } = this.props;
    const { isOpen } = this.state;

    let renderPropChildren = children;
    if (_isFunction(children)) {
      renderPropChildren = children({
        closeMenu: this.closeMenu,
        disabled,
        isOpen,
        toggleMenu: this.toggleMenu,
        id: this.toggleId,
      });
      if (isFragment(renderPropChildren)) {
        renderPropChildren = renderPropChildren.props.children;
      }
    }

    return React.Children.toArray(renderPropChildren);
  }

  makeItemIdGenerator = () => {
    let itemId = 0;
    // this will be unique for each item and re-render, always starting from 0
    return () => {
      const instanceId = `Dropdown_${this.instanceId}_Item_${(itemId += 1)}`;
      return instanceId;
    };
  };

  toggleMenu = (event, callback) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { disabled } = this.props;
    const { isOpen } = this.state;

    if (isOpen) {
      this.closeMenu();
      return;
    }

    if (!disabled) {
      const { onOpen } = this.props;
      this.setState({ isOpen: true }, () => {
        this.adjustSizeAndPosition();
        this.scrollToSelectedItem();
        if (onOpen) onOpen();
        if (callback) callback();
      });
    }
  };

  closeMenu = () => {
    const { onClose, selectedOption } = this.props;

    this.setState(
      {
        isOpen: false,
        highlightedOption: selectedOption,
      },
      () => {
        this.resetInputValue();
        if (onClose) {
          onClose();
        }
      }
    );
  };

  openMenu = callback => {
    const { isOpen } = this.state;
    if (!isOpen) this.toggleMenu(null, callback);
  };

  handleSelect = () => {
    const { closeOnSelect, multiselect } = this.props;

    if (!multiselect) {
      this.resetInputValue();
    }

    if (closeOnSelect) {
      const { first } = getFocusableChildren(this.containerRef.current);
      first?.focus();
      this.closeMenu();
    }
  };

  handleOverlayClick = event => {
    event?.stopPropagation();

    const { closeOnClick } = this.props;
    const { isOpen } = this.state;

    if (closeOnClick && isOpen) {
      this.closeMenu();
    }
  };

  handleToggleKeyEvent = event => {
    const { closeOnEsc, multiselect, autocomplete } = this.props;
    const { isOpen } = this.state;
    const { nativeEvent, shiftKey } = event;
    const keyCode = nativeEvent?.code?.toUpperCase();

    switch (keyCode) {
      case 'SPACE':
      case 'ENTER': {
        if (!multiselect && !autocomplete) event.preventDefault();
        this.toggleMenu();
        break;
      }

      case 'TAB': {
        if (isOpen && shiftKey) {
          this.closeMenu();
        } else if (isOpen) {
          event.preventDefault();
          const { first } = getFocusableChildren(this.scrollContainerRef.current);
          first?.focus();
        }
        break;
      }

      case 'ARROWDOWN': {
        event.preventDefault();
        this.openMenu(() => {
          const { first } = getFocusableChildren(this.scrollContainerRef.current);
          first?.focus();
        });
        break;
      }

      case 'ESCAPE': {
        this.resetInputValue();
        if (closeOnEsc && isOpen) this.closeMenu();
        break;
      }

      default:
        if (autocomplete) this.openMenu();
        break;
    }
  };

  handleContentKeyEvent = event => {
    const { closeOnEsc } = this.props;
    const { isOpen, filteredOptions, highlightedOption } = this.state;
    const { nativeEvent, shiftKey, target } = event;
    const keyCode = nativeEvent?.code?.toUpperCase();

    const highlightedIndex = _findIndex(filteredOptions, option => _isEqual(option, highlightedOption));
    let nextIndex = null;
    let nextHighlightedOption;

    if (keyCode === 'ARROWDOWN') {
      nextIndex = (highlightedIndex + 1) % filteredOptions.length;
    } else if (keyCode === 'ARROWUP') {
      nextIndex = highlightedIndex === 0 ? filteredOptions.length - 1 : highlightedIndex - 1;
    }

    if (nextIndex !== null) {
      nextHighlightedOption = filteredOptions.length === 0 ? null : filteredOptions[nextIndex];
    }

    switch (keyCode) {
      case 'ESCAPE': {
        if (closeOnEsc && isOpen) {
          const { first } = getFocusableChildren(this.containerRef.current);
          first?.focus();
          this.closeMenu();
        }
        break;
      }

      case 'TAB': {
        const { first, last } = getFocusableChildren(this.scrollContainerRef.current);
        if ((shiftKey && target === first) || (!shiftKey && target === last)) {
          event.preventDefault();
          const { first: containerTarget } = getFocusableChildren(this.containerRef.current);
          containerTarget?.focus();
          this.closeMenu();
        }
        break;
      }

      case 'ARROWDOWN':
      case 'ARROWUP': {
        event.preventDefault();
        if (nextHighlightedOption) {
          const match = this.getKeyForOption(nextHighlightedOption);
          const current = _path(['itemRefs', match, 'current'], this);
          if (current) {
            current.focus();
          }
          this.setState({ highlightedOption: nextHighlightedOption });
        }
        break;
      }

      default:
        break;
    }
  };

  resetInputValue = () => {
    this.setInputValue(null);
  };

  setInputValue = inputValue => {
    const { options, optionsFilter, autocomplete, intl } = this.props;
    const { highlightedOption } = this.state;

    let filteredOptions =
      !inputValue || inputValue.trim() === ''
        ? options
        : options.filter(option => {
            const key = this.getKeyForOption(option);
            const isFilterable =
              _isObject(option) && Object.keys(option).includes('isFilterable') ? option.isFilterable : true;
            return !isFilterable || optionsFilter(inputValue, key);
          });
    filteredOptions =
      autocomplete && !filteredOptions.length ? [{ label: intl.formatMessage(messages.noMatch) }] : filteredOptions;

    const highlightedOptionStillValid = filteredOptions.some(
      option => this.getKeyForOption(option) === this.getKeyForOption(highlightedOption)
    );

    let nextHighlightedOption;
    if (highlightedOptionStillValid) {
      nextHighlightedOption = highlightedOption;
    } else if (filteredOptions.length > 0) {
      [nextHighlightedOption] = filteredOptions;
    } else {
      nextHighlightedOption = null;
    }

    this.setState({
      inputValue,
      filteredOptions,
      highlightedOption: nextHighlightedOption,
    });
  };

  handleInputChange = event => {
    event.stopPropagation();
    this.setInputValue(event.target.value);
  };

  handleInputClick = event => {
    event.stopPropagation();

    const { isOpen } = this.state;

    if (!isOpen) {
      this.toggleMenu();
    }
  };

  handleInputFocus = () => {
    const { inputValue } = this.state;

    if (inputValue === null) {
      this.setInputValue('');
    }
  };

  handleInputBlur = () => {
    const { inputValue } = this.state;

    if (inputValue === '') {
      this.resetInputValue();
    }
  };

  handleOnFocus = event => {
    event.stopPropagation();

    const { current: input } = this.inputRef;

    if (input) {
      input.focus();
    }
  };

  // getKeyForOption - dynamically generated by makeRefsForOptions

  makeRefsForOptions(options) {
    const noOptionsKey = 'no-options';
    const getKey = (hasOptions, option) => {
      if (!hasOptions) {
        return noOptionsKey;
      }

      if (_isObject(option) && option.match) {
        return option.match;
      }

      return JSON.stringify(option);
    };

    this.getKeyForOption = getKey.bind(null, !!options.length);

    if (!options.length) {
      return {
        [noOptionsKey]: React.createRef(),
      };
    }

    return options.reduce((refs, option) => {
      const key = this.getKeyForOption(option);
      return {
        ...refs,
        [key]: React.createRef(),
      };
    }, {});
  }

  adjustSizeAndPosition() {
    const { contentDoesntFitBelowToggle } = this.state;
    const { isAlignTopToggleStyle, usePortal, isDesktop, attachment, deriveContentWidth = Math.max } = this.props;

    if (!usePortal || !isDesktop) {
      return;
    }

    const { current: container } = this.containerRef;
    const { current: scrollContainer } = this.scrollContainerRef;

    if (!container || !scrollContainer) {
      return;
    }

    const {
      top: toggleTop,
      right: toggleRight,
      bottom: toggleBottom,
      left: toggleLeft,
      width: toggleWidth,
    } = container.getBoundingClientRect();
    const { width: contentWidth, height: contentHeight } = scrollContainer.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const contentStyle = window.getComputedStyle(scrollContainer);
    const verticalMargins =
      Number.parseInt(contentStyle.marginTop, 10) + Number.parseInt(contentStyle.marginBottom, 10);
    const derivedContentWidth = deriveContentWidth(
      Math.floor(toggleWidth),
      Math.floor(contentWidth),
      Number.parseInt(contentStyle.minWidth, 10)
    );
    const centerDropdownPlacement = `${
      toggleLeft - Math.floor(derivedContentWidth / 2) + Math.floor(toggleWidth / 2)
    }px`;

    const contentDoesntFitOnScreen = contentHeight > windowHeight;
    const contentDoesntFitBelowTheToggle = contentHeight > windowHeight - toggleBottom;
    const contentDoesntFitAboveTheToggle = contentHeight > toggleTop;
    const contentDoesntFitRightSideOfToggle = contentWidth > windowWidth - toggleRight;

    if (contentDoesntFitBelowTheToggle !== contentDoesntFitBelowToggle) {
      this.setState({ contentDoesntFitBelowToggle: contentDoesntFitBelowTheToggle });
    }

    if (contentDoesntFitOnScreen) {
      // constrain to the top and bottom of the screen
      scrollContainer.style.top = '0px';
      scrollContainer.style.height = `${windowHeight - verticalMargins}px`;
    } else if (contentDoesntFitBelowTheToggle && contentDoesntFitAboveTheToggle) {
      // attach to the bottom of the screen and extend up
      scrollContainer.style.top = `${windowHeight - contentHeight - verticalMargins}px`;
    } else if (contentDoesntFitBelowTheToggle) {
      // attach to the top of the toggle and extend up
      scrollContainer.style.bottom = `${windowHeight - toggleTop}px`;
    } else if (isAlignTopToggleStyle) {
      // subtract an extra pixel to prevent the toggle highlight from showing
      scrollContainer.style.top = `${toggleTop - Number.parseInt(contentStyle.marginTop, 10) - 1}px`;
    } else {
      // attach to the bottom of the toggle and extend down
      scrollContainer.style.top = `${toggleBottom}px`;
    }

    scrollContainer.style.position = 'fixed';
    scrollContainer.style.minWidth = `${derivedContentWidth}px`;

    switch (attachment) {
      case 'left':
        scrollContainer.style.left = `${toggleLeft}px`;
        break;

      case 'pinRight':
        if (!contentDoesntFitRightSideOfToggle) {
          scrollContainer.style.left = `${toggleRight}px`;
        } else {
          scrollContainer.style.left = centerDropdownPlacement;
        }
        break;
      case 'right':
        scrollContainer.style.left = `${toggleRight - derivedContentWidth}px`;
        break;

      default:
        // 'center'
        scrollContainer.style.left = centerDropdownPlacement;
        break;
    }
  }

  scrollToSelectedItem() {
    const { selectedOption } = this.props;
    const key = this.getKeyForOption(selectedOption);

    const { current: selectedItem } = this.itemRefs[key] || {};
    const { current: scrollContainer } = this.scrollContainerRef;

    if (selectedItem && scrollContainer) {
      const { offsetHeight: scrollContainerHeight } = scrollContainer;
      const { offsetHeight: selectedItemHeight } = selectedItem;

      // Center the selected item in the scroll container
      const scrollTop = selectedItem.offsetTop - scrollContainerHeight / 2 + selectedItemHeight / 2;
      scrollContainer.scrollTop = scrollTop;
    }
  }

  keepItemVisible(option) {
    const key = this.getKeyForOption(option);
    const { current: selectedItem } = this.itemRefs[key] || {};

    if (selectedItem) {
      selectedItem.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  renderToggle() {
    const { disabled, autocomplete, selectedOption, intl } = this.props;
    const { isOpen, inputValue } = this.state;
    const [toggleChild] = this.getToggleAndContent();
    const placeholder = _get(toggleChild, 'props.placeholder');

    // displayValue should never equal null - it will trigger uncontrolled -> controlled errors
    let displayValue;
    if (inputValue !== null) {
      displayValue = inputValue;
    } else if (autocomplete) {
      displayValue = selectedOption.label || '';
    } else {
      displayValue = '';
    }

    const additionalChildren = !autocomplete
      ? []
      : [
          <input
            type="text"
            ref={this.inputRef}
            key="autocomplete-input"
            value={displayValue}
            disabled={disabled}
            onChange={this.handleInputChange}
            onClick={this.handleInputClick}
            onFocus={this.handleInputFocus}
            onBlur={this.handleInputBlur}
            placeholder={!placeholder ? '' : intl.formatMessage(placeholder)}
            autoComplete="off"
            className={element('autocomplete-input')}
          />,
        ];

    return (
      <div
        className={element('toggle', { disabled, isOpen })}
        role="button"
        aria-haspopup="listbox"
        aria-controls={this.menuId}
        aria-label={intl.formatMessage(messages.defaultLabel)}
        aria-labelledby={this.toggleId}
        tabIndex={autocomplete ? '-1' : '0'}
        aria-expanded={isOpen}
        onFocus={this.handleOnFocus}
        onKeyDown={this.handleToggleKeyEvent}>
        {isNativeElement(toggleChild)
          ? toggleChild
          : React.cloneElement(
              toggleChild,
              {
                toggleMenu: this.toggleMenu,
                closeMenu: this.closeMenu,
                isOpen,
                isAutocomplete: autocomplete,
                disabled,
                id: this.toggleId,
              },
              ...additionalChildren
            )}
      </div>
    );
  }

  renderContent() {
    const {
      closeOnScroll,
      contentClassName,
      disabled,
      isLargeRadiusStyle,
      isSmallRadiusStyle,
      portalId,
      useOverlay,
      usePortal,
    } = this.props;
    const { isOpen, filteredOptions, highlightedOption } = this.state;
    const [, contentChild] = this.getToggleAndContent();
    const portalTarget = document.getElementById(portalId);
    const highlightedKey = this.getKeyForOption(highlightedOption);
    const renderPropData = filteredOptions.map(option => {
      const optionKey = this.getKeyForOption(option);
      const ref = this.itemRefs[optionKey] || React.createRef(); // fallback prevents console noise
      return {
        option,
        ref,
        isHighlighted: optionKey === highlightedKey,
      };
    });

    /* TODO add `aria-activedescendent` (if we refactor Dropdown).
    This problem would be much easier if Dropdown controlled the full state,
    and the toggle/menu/items were passive consumers of it (via context).
    As it is now, the Dropdown just controls the menu but has no idea which item
    is selected. Screen readers can already deduce which item is active via
    `aria-selected` in DropdownItem, so we've punted on this for now. */

    let content = null;
    if (isOpen) {
      content = [
        <Overlay
          key="overlay"
          className={element('overlay')}
          closeOnScroll={closeOnScroll}
          onClick={this.handleOverlayClick}
        />,
        <div
          key="container"
          id={this.instanceId}
          role="listbox"
          aria-label={this.instanceId}
          tabIndex="-1"
          ref={this.scrollContainerRef}
          className={element('content', contentClassName, { isLargeRadiusStyle, isSmallRadiusStyle, usePortal })}
          onKeyDown={this.handleContentKeyEvent}>
          {isNativeElement(contentChild)
            ? contentChild
            : React.cloneElement(contentChild, {
                closeMenu: this.closeMenu,
                disabled,
                fallbackSelectedItemRef: this.itemRefs[this.getKeyForOption()],
                isOpen,
                onSelect: this.handleSelect,
                renderPropData,
                toggleMenu: this.toggleMenu,
                id: this.menuId,
                getItemId: this.makeItemIdGenerator(),
              })}
        </div>,
      ];

      if (!useOverlay || !usePortal || !portalTarget) {
        content.shift();
      }
    }

    if (!usePortal || !portalTarget) {
      return content;
    }

    return ReactDOM.createPortal(content, portalTarget);
  }

  render() {
    const { className, dataCypress, showMenuCaret } = this.props;
    const { isOpen, contentDoesntFitBelowToggle } = this.state;
    const dropdownContext = {
      toggleId: this.toggleId,
      toggleMenu: this.toggleMenu,
      menuId: this.menuId,
      getItemId: this.makeItemIdGenerator(),
      showMenuCaret: showMenuCaret && !contentDoesntFitBelowToggle,
    };

    return (
      <div ref={this.containerRef} className={block({ isOpen }, className)} data-cypress={dataCypress}>
        <DropdownContext.Provider value={dropdownContext}>
          {this.renderToggle()}
          {this.renderContent()}
        </DropdownContext.Provider>
      </div>
    );
  }
}

export default Dropdown;
